import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Shield, Eye } from 'lucide-react';

interface CookieConsentProps {
  onAccept: () => void;
  onReject: () => void;
  onCustomize: () => void;
}

export interface CookiePreferences {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const STORAGE_KEY = 'cookie-consent-preferences';
const CONSENT_VERSION = '1.0';

// eslint-disable-next-line react-refresh/only-export-components
export const useCookieConsent = () => {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data.version === CONSENT_VERSION) {
          setPreferences(data.preferences);
        } else {
          // Version changed, show banner again
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    const data = {
      preferences: prefs,
      version: CONSENT_VERSION,
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setPreferences(prefs);
    setShowBanner(false);
    setShowCustomize(false);
  };

  const acceptAll = () => {
    savePreferences({
      analytics: true,
      marketing: false, // We don't use marketing cookies
      functional: true
    });
  };

  const rejectAll = () => {
    savePreferences({
      analytics: false,
      marketing: false,
      functional: true // Keep functional for basic site operation
    });
  };

  const customize = () => {
    setShowCustomize(true);
  };

  return {
    preferences,
    showBanner,
    showCustomize,
    setShowCustomize,
    acceptAll,
    rejectAll,
    customize,
    savePreferences
  };
};

const CookieCustomization: React.FC<{
  onSave: (preferences: CookiePreferences) => void;
  onClose: () => void;
}> = ({ onSave, onClose }) => {
  const [prefs, setPrefs] = useState<CookiePreferences>({
    analytics: false,
    marketing: false,
    functional: true
  });

  const handleSave = () => {
    onSave(prefs);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Customize Cookie Preferences
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Functional Cookies */}
          <div className="flex items-start space-x-3">
            <div className="mt-1">
              <input
                type="checkbox"
                checked={prefs.functional}
                disabled
                className="w-4 h-4 text-blue-600 rounded opacity-50"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <Shield className="w-4 h-4 text-green-500" />
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Functional Cookies
                </h4>
                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-0.5 rounded">
                  Required
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Essential for website functionality, theme preferences, and user experience.
              </p>
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-start space-x-3">
            <div className="mt-1">
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <Eye className="w-4 h-4 text-blue-500" />
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Analytics Cookies
                </h4>
                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">
                  Optional
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Help me understand how you use my website through privacy-focused analytics. No personal data is collected.
              </p>
            </div>
          </div>

          {/* Marketing Cookies */}
          <div className="flex items-start space-x-3 opacity-50">
            <div className="mt-1">
              <input
                type="checkbox"
                checked={false}
                disabled
                className="w-4 h-4 text-blue-600 rounded opacity-50"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <Settings className="w-4 h-4 text-gray-400" />
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Marketing Cookies
                </h4>
                <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                  Not Used
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                I don't use marketing or advertising cookies on this website.
              </p>
            </div>
          </div>
        </div>

        <div className="flex space-x-3 mt-6">
          <button
            onClick={handleSave}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Save Preferences
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onReject, onCustomize }) => {
  const {
    showBanner,
    showCustomize,
    setShowCustomize,
    acceptAll,
    rejectAll,
    customize,
    savePreferences
  } = useCookieConsent();

  const handleAccept = () => {
    acceptAll();
    onAccept();
  };

  const handleReject = () => {
    rejectAll();
    onReject();
  };

  const handleCustomize = () => {
    customize();
    onCustomize();
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="fixed bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 z-50"
          >
            <div className="max-w-6xl mx-auto">
              <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl">
                
                {/* Desktop Layout */}
                <div className="hidden sm:block px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <Cookie className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        🍪 I use privacy-focused analytics to learn from traffic. No personal data collected.
                      </span>
                      <a href="/privacy" className="text-blue-600 hover:text-blue-700 underline text-sm whitespace-nowrap">
                        Privacy Policy
                      </a>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={handleAccept}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-1.5 px-3 rounded transition-colors"
                      >
                        Accept
                      </button>
                      <button
                        onClick={handleReject}
                        className="bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 text-xs font-medium py-1.5 px-3 rounded transition-colors"
                      >
                        Reject
                      </button>
                      <button
                        onClick={handleCustomize}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 text-xs font-medium py-1.5 px-3 rounded border border-blue-200 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        Settings
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mobile Layout */}
                <div className="block sm:hidden p-3">
                  <div className="flex items-start space-x-3 mb-2">
                    <Cookie className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        🍪 I use privacy-focused analytics to learn from traffic. No personal data collected.
                      </p>
                      <a href="/privacy" className="text-blue-600 hover:text-blue-700 underline text-sm inline-block mt-1">
                        Privacy Policy
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={handleAccept}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-3 rounded transition-colors"
                    >
                      Accept
                    </button>
                    <button
                      onClick={handleReject}
                      className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium py-2 px-3 rounded transition-colors"
                    >
                      Reject
                    </button>
                    <button
                      onClick={handleCustomize}
                      className="px-3 py-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 text-sm font-medium rounded border border-blue-200 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCustomize && (
          <CookieCustomization
            onSave={savePreferences}
            onClose={() => setShowCustomize(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;
